'use client';

import { MouseEventHandler, ReactElement } from 'react';
import { clsx } from 'clsx';

import DallasSvgIcon from '../../Icons/DallasSvgIcon';

import styles from './DallasFormControlIconButton.module.scss';
import DallasTooltipAnchor from '../../Tooltip/DallasTooltipAnchor';

export interface Props {
  /**
   * Accessibility label for the button.
   */
  'aria-label': string;
  /**
   * DallasSvgIcon element at the center position of the button
   */
  icon: ReactElement<typeof DallasSvgIcon>;
  /**
   * Optional className for positioning and identification purposes, button styles will override conflicting styles
   */
  className?: string;
  /**
   * Size of the button
   */
  size?: 'small' | 'tiny' | 'paddingless';
  /**
   * Optional click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Specifies that the corners should be rounded.
   */
  rounded?: boolean;
  /**
   * Specifies that the button should be disabled.
   */
  disabled?: boolean;
  /**
   * Optional tab index
   */
  tabIndex?: number;
  /**
   * Optional prop to render tooltip in a portal
   * when a tooltip anchor is in a container wher overflow is hidden
   */
  tooltipPortal?: boolean;
}

export default function DallasFormControlIconButton({
  icon,
  'aria-label': ariaLabel,
  onClick,
  className,
  size = 'tiny',
  rounded = false,
  disabled = false,
  tabIndex,
  tooltipPortal,
}: Props) {
  return (
    <DallasTooltipAnchor
      tooltip={ariaLabel}
      tooltipType="label"
      tooltipPortal={tooltipPortal}
      aria-label={ariaLabel}
      render={({ color: _, onClick: _onClick, ...props }) => (
        <button
          {...props}
          type="button"
          className={clsx(
            className,
            styles.root,
            styles[`size_${size}`],
            rounded
              ? styles.borderRadius_rounded
              : styles.borderRadius_semiRounded,
          )}
          onClick={onClick}
          disabled={disabled}
          aria-label={ariaLabel}
          tabIndex={tabIndex}
        >
          {icon}
        </button>
      )}
    />
  );
}
